/*!
 * Oneskin Quick View Modal — vanilla JS controller for the site-wide quick view
 * modal (`[data-quick-view-modal]`, rendered once via snippets/quick-view-modal.liquid).
 *
 * Detached from the frontend/ Vite bundle. Loaded directly via:
 *   <script src="{{ 'quick-view-modal.js' | asset_url }}" defer="defer"></script>
 */
;(() => {
  'use strict'

  function initQuickViewModal(node) {
    const wash = node.querySelector('.modal__wash')
    const closeButton = node.querySelector('[data-modal-close]')
    const modalContent = node.querySelector('.quick-view-modal__content')
    const loadingMessage = node.querySelector('.quick-view-modal-loading-indicator')

    let product = null
    let quickViewOpened = false
    let quickViewAdded = false
    const events = []

    if (wash) {
      const washHandler = (e) => {
        e.preventDefault()
        close()
      }
      wash.addEventListener('click', washHandler)
      events.push(() => wash.removeEventListener('click', washHandler))
    }

    if (closeButton) {
      const closeHandler = (e) => {
        e.preventDefault()
        close()
      }
      closeButton.addEventListener('click', closeHandler)
      events.push(() => closeButton.removeEventListener('click', closeHandler))
    }

    const keydownHandler = (e) => {
      if (e.key === 'Escape') close()
    }
    node.addEventListener('keydown', keydownHandler)
    events.push(() => node.removeEventListener('keydown', keydownHandler))

    function clearProductCardClones() {
      document.querySelectorAll('.js-quick-add-wrapper').forEach((wrapper) => wrapper.classList.remove('active'))
    }

    function showLoading() {
      if (!modalContent) return
      modalContent.classList.add('empty')
      modalContent.innerHTML = ''
      if (loadingMessage) {
        modalContent.appendChild(loadingMessage.cloneNode(true))
      }
    }

    function renderProductContent(productUrl) {
      if (!productUrl || !modalContent) return

      const xhrUrl = `${productUrl}${productUrl.includes('?') ? '&' : '?'}view=quick-view`
      showLoading()

      fetch(xhrUrl, { credentials: 'include' })
        .then((res) => {
          if (!res.ok) throw new Error(`Quick view request failed (${res.status})`)
          return res.text()
        })
        .then((response) => {
          const doc = new DOMParser().parseFromString(response, 'text/html')
          const productElement = doc.querySelector('[data-is-quick-view]')
          if (!productElement) {
            console.error('[quick-view-modal] [data-is-quick-view] not found in response')
            return
          }

          productElement.addEventListener('submit', () => {
            quickViewAdded = true
          })

          const siblingElements = Array.from(productElement.querySelectorAll('.product__color-swatch--sibling-product'))
          siblingElements.forEach((sibling) => {
            const siblingUrl = sibling.getAttribute('href') || sibling.pathname
            if (!siblingUrl) return
            const siblingHandler = (e) => {
              e.preventDefault()
              e.stopPropagation()
              document.dispatchEvent(new CustomEvent('quick-view:refresh', { detail: { productUrl: siblingUrl } }))
            }
            sibling.addEventListener('click', siblingHandler)
            events.push(() => sibling.removeEventListener('click', siblingHandler))
          })

          product?.unload?.()
          modalContent.classList.remove('empty')
          modalContent.replaceChildren(productElement)

          node.classList.add('animation--quick-view-revealed')

          const renderedProductElement = modalContent.querySelector('[data-is-quick-view]')
          if (renderedProductElement && window.OneskinProduct) {
            product = new window.OneskinProduct(renderedProductElement)
          }

          document.dispatchEvent(new CustomEvent('product-item:reinit', { detail: { root: modalContent } }))
        })
        .catch((err) => console.error('[quick-view-modal] failed to load product', err))
    }

    function lockScroll() {
      document.documentElement.classList.add('overflow-hidden')
      document.body.classList.add('overflow-hidden')
    }

    function unlockScroll() {
      document.documentElement.classList.remove('overflow-hidden')
      document.body.classList.remove('overflow-hidden')
    }

    function open() {
      clearProductCardClones()
      quickViewOpened = true
      quickViewAdded = false
      node.classList.add('is-fixed', 'active', 'is-visible', 'animation--quick-view-revealed')
      node.removeAttribute('aria-hidden')
      lockScroll()
      document.body.setAttribute('data-fluorescent-overlay-open', 'true')
    }

    function close() {
      if (quickViewOpened && !quickViewAdded && window.oneskin && window.oneskin.events) {
        const title =
          node.querySelector('[data-product-form]')?.dataset.productTitle ||
          node.querySelector('[data-product-title]')?.getAttribute('data-product-title') ||
          ''
        window.oneskin.events.track('quick_add_abandon', {
          product_name: title,
          source_location: 'quick_view',
        })
      }
      quickViewOpened = false
      quickViewAdded = false
      clearProductCardClones()
      const sheet = node.querySelector('.quick-product--modal__inner')
      if (sheet) {
        sheet.classList.remove('is-dragging')
        sheet.style.transform = ''
      }
      node.classList.remove('is-visible', 'active')
      node.setAttribute('aria-hidden', 'true')
      document.body.setAttribute('data-fluorescent-overlay-open', 'false')
      unlockScroll()

      setTimeout(() => {
        node.classList.remove('is-fixed')
        if (modalContent) {
          modalContent.innerHTML = ''
          if (loadingMessage) modalContent.appendChild(loadingMessage)
          modalContent.classList.add('empty')
        }
        product?.unload?.()
        product = null
      }, 50)
    }

    function initSwipeToClose() {
      const sheet = node.querySelector('.quick-product--modal__inner')
      if (!sheet) return

      const CLOSE_DISTANCE = 110
      const CLOSE_VELOCITY = 0.55
      let startY = 0
      let startX = 0
      let currentY = 0
      let startTime = 0
      let tracking = false
      let dragging = false

      function isMobileSheet() {
        return window.matchMedia('(max-width: 719px)').matches
      }

      function getScrollableAncestor(target) {
        let el = target instanceof Element ? target : null
        while (el && el !== sheet) {
          const style = window.getComputedStyle(el)
          const canScrollY = /(auto|scroll)/.test(style.overflowY) || /(auto|scroll)/.test(style.overflow)
          if (canScrollY && el.scrollHeight > el.clientHeight + 1) return el
          el = el.parentElement
        }
        return sheet.querySelector('.quick-product--scroll')
      }

      function resetDrag() {
        tracking = false
        dragging = false
        currentY = 0
        sheet.classList.remove('is-dragging')
        sheet.style.transform = ''
      }

      function onTouchStart(e) {
        if (!isMobileSheet() || !node.classList.contains('active')) return
        if (e.touches.length !== 1) return

        const touch = e.touches[0]
        startY = touch.clientY
        startX = touch.clientX
        currentY = 0
        startTime = Date.now()
        dragging = false

        const scrollEl = getScrollableAncestor(e.target)
        tracking = !(scrollEl && scrollEl.scrollTop > 0)
      }

      function onTouchMove(e) {
        if (!tracking) return

        const touch = e.touches[0]
        const dy = touch.clientY - startY
        const dx = touch.clientX - startX

        if (!dragging) {
          if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 10) {
            tracking = false
            return
          }
          if (dy <= 0) {
            tracking = false
            return
          }
          if (dy > 8) {
            dragging = true
            sheet.classList.add('is-dragging')
          } else {
            return
          }
        }

        if (dy > 0) {
          currentY = dy
          sheet.style.transform = `translateY(${dy}px)`
          e.preventDefault()
        }
      }

      function onTouchEnd() {
        if (!tracking && !dragging) return

        const elapsed = Math.max(Date.now() - startTime, 1)
        const velocity = currentY / elapsed
        const shouldClose = dragging && (currentY >= CLOSE_DISTANCE || velocity >= CLOSE_VELOCITY)

        sheet.classList.remove('is-dragging')

        if (shouldClose) {
          sheet.style.transform = ''
          close()
        } else {
          sheet.style.transform = ''
        }

        tracking = false
        dragging = false
        currentY = 0
      }

      sheet.addEventListener('touchstart', onTouchStart, { passive: true })
      sheet.addEventListener('touchmove', onTouchMove, { passive: false })
      sheet.addEventListener('touchend', onTouchEnd)
      sheet.addEventListener('touchcancel', onTouchEnd)

      events.push(
        () => sheet.removeEventListener('touchstart', onTouchStart),
        () => sheet.removeEventListener('touchmove', onTouchMove),
        () => sheet.removeEventListener('touchend', onTouchEnd),
        () => sheet.removeEventListener('touchcancel', onTouchEnd),
        () => resetDrag()
      )
    }

    initSwipeToClose()

    const openHandler = (e) => {
      const { productUrl } = e.detail || {}
      if (!productUrl) return
      open()
      renderProductContent(productUrl)
    }
    const closeEventHandler = () => close()
    const refreshHandler = (e) => {
      if (e.detail?.productUrl) renderProductContent(e.detail.productUrl)
    }

    document.addEventListener('quick-view:open', openHandler)
    document.addEventListener('quick-view:close', closeEventHandler)
    document.addEventListener('quick-view:refresh', refreshHandler)
    events.push(
      () => document.removeEventListener('quick-view:open', openHandler),
      () => document.removeEventListener('quick-view:close', closeEventHandler),
      () => document.removeEventListener('quick-view:refresh', refreshHandler)
    )

    return {
      unload() {
        events.forEach((off) => off())
      },
    }
  }

  function autoInit() {
    const node = document.querySelector('[data-quick-view-modal]')
    if (!node) return
    initQuickViewModal(node)
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', autoInit)
  } else {
    autoInit()
  }
})()
